import React, { useState, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Form,
  Input,
  Select,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { SourceGlossary } from '../../types';
import { sourceStoriesApi, CreateSourceGlossaryRequest, UpdateSourceGlossaryRequest } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface GlossaryTabProps {
  storyId: string;
}

const GlossaryTab: React.FC<GlossaryTabProps> = ({ storyId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTerm, setEditingTerm] = useState<SourceGlossary | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch glossary terms
  const {
    data: glossaryData,
    loading: glossaryLoading,
    refresh: refreshGlossary,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceGlossaries(storyId, {
      originalTerm: searchText || undefined,
      type: selectedType || undefined,
      pageSize: 500
    }), [storyId, searchText, selectedType]),
    { immediate: true }
  );

  const handleCreate = () => {
    setEditingTerm(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (term: SourceGlossary) => {
    setEditingTerm(term);
    form.setFieldsValue(term);
    setIsModalVisible(true);
  };

  const handleDelete = async (term: SourceGlossary) => {
    await executeAsync(
      () => sourceStoriesApi.deleteSourceGlossary(storyId, term.id),
      { successMessage: 'Term deleted successfully!' }
    );
    refreshGlossary();
  };

  const handleSubmit = async (values: any) => {
    if (editingTerm) {
      await executeAsync(
        () => sourceStoriesApi.updateSourceGlossary(storyId, editingTerm.id, values as UpdateSourceGlossaryRequest),
        { successMessage: 'Term updated successfully!' }
      );
    } else {
      await executeAsync(
        () => sourceStoriesApi.createSourceGlossary(storyId, values as CreateSourceGlossaryRequest),
        { successMessage: 'Term created successfully!' }
      );
    }
    setIsModalVisible(false);
    refreshGlossary();
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'LOCATION':
        return 'blue';
      case 'ORGANIZATION':
        return 'purple';
      case 'ITEM':
        return 'green';
      case 'CONCEPT':
        return 'cyan';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Original Term',
      dataIndex: 'originalTerm',
      key: 'originalTerm',
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
      sorter: (a: SourceGlossary, b: SourceGlossary) => a.originalTerm.localeCompare(b.originalTerm),
    },
    {
      title: 'Localized Term',
      dataIndex: 'localizedTerm',
      key: 'localizedTerm',
      render: (text: string) => <Typography.Text type="success">{text}</Typography.Text>,
      sorter: (a: SourceGlossary, b: SourceGlossary) => a.localizedTerm.localeCompare(b.localizedTerm),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type}</Tag>
      ),
      filters: [
        { text: 'Location', value: 'LOCATION' },
        { text: 'Organization', value: 'ORGANIZATION' },
        { text: 'Item', value: 'ITEM' },
        { text: 'Concept', value: 'CONCEPT' },
        { text: 'Other', value: 'OTHER' },
      ],
      onFilter: (value: any, record: SourceGlossary) => record.type === value,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Last Modified',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      render: (date: string) => formatDate(date),
      sorter: (a: SourceGlossary, b: SourceGlossary) => new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: SourceGlossary) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this term?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Localization Glossary
        </Title>
        <Space>
          <Input
            placeholder="Search original term..."
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <Select
            placeholder="Filter by type"
            style={{ width: 150 }}
            onChange={setSelectedType}
            allowClear
          >
            <Option value="LOCATION">Location</Option>
            <Option value="ORGANIZATION">Organization</Option>
            <Option value="ITEM">Item</Option>
            <Option value="CONCEPT">Concept</Option>
            <Option value="OTHER">Other</Option>
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Term
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={glossaryData?.data || []}
        rowKey="id"
        loading={glossaryLoading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} terms`,
        }}
      />

      <Modal
        title={editingTerm ? 'Edit Glossary Term' : 'Add New Glossary Term'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ type: 'OTHER' }}
        >
          <Form.Item
            name="originalTerm"
            label="Original Term"
            rules={[{ required: true, message: 'Please enter original term' }]}
          >
            <Input placeholder="e.g. Phúc Thọ Đường" />
          </Form.Item>

          <Form.Item
            name="localizedTerm"
            label="Localized Term"
            rules={[{ required: true, message: 'Please enter localized term' }]}
          >
            <Input placeholder="e.g. Spirit Shop" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <Select placeholder="Select type">
              <Option value="LOCATION">Location</Option>
              <Option value="ORGANIZATION">Organization</Option>
              <Option value="ITEM">Item</Option>
              <Option value="CONCEPT">Concept</Option>
              <Option value="OTHER">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Additional context for this term..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                {editingTerm ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default GlossaryTab;
